/* eslint-disable max-lines */
import { useContext, useState, useRef, useEffect, useCallback } from "react";
import {
    ApiContext,
    CheckoutActionTypes,
    CheckoutContext,
    getBridgeSdk,
    GlobalHeaderState,
    NEWRELIC_TABLES,
    recordNREvent,
    SwgyHeaderKeysList,
    GroupOrderCartContext,
} from "@swiggy-private/checkout-core";
import { captureError, isEmpty, isNetworkError, useMountedRef, randomStringIdGenerator } from "@swiggy-private/core-ui";

// reducers
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// helpers
import {
    getCrossSellM2RequestBodyItems,
    getRetainedCartOrderId,
    prepareNativeCartParamsFromOrderDetails,
    prepareNativeCartParamsFromReviewedCartResponse,
    buildCartJourneyContext,
} from "@cart-client/helpers/Cart";
import { getDateFromApiResponse } from "@cart-client/helpers/BulkOrder";
import { isUserLoggedIn } from "@cart-client/helpers/User";
import { cloneDeep } from "@swiggy-private/js-utils";
import { isClosingTimeAvailable, setClosingTimeInStorage } from "@cart-client/helpers/TimerOnCart";

// apis
import { getCart, getSingleOrder } from "@cart-client/apis/Cart";
import { updateCartPublic, logUpdateCartEvent } from "@cart-client/apis/publicCart";

// interfaces
import { ReviewedCart } from "@cart-client/interfaces/Cart";
import { Ok, Err, Result, Some } from "@swiggy-private/js-fp";
import { ApiResponse } from "@cart-client/interfaces/Api";
import { ReviewedCartApiResponse, NativeLocalCart, CUSTOM_EVENTS } from "@swiggy-private/cart-types";

// constants
import { TIMER_RESPONSE_SOURCE } from "@cart-client/constants/NewRelicEvents";
import { HTTP_STATUS_CODES } from "@cart-client/constants/index";
import { OOPS_SOMETHING_WENT_WRONG } from "@cart-client/constants/TextDict";

// hooks
import { useLocalCartTransformer } from "@cart-client/hooks/useLocalCartTransformer";

// contexts
import { CrossSellM2Context } from "@cart-client/contexts/CrossSellM2Context";
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartToastContext } from "@cart-client/contexts/CartToastContext";

interface CartState {
    loading: boolean;
    localCartState: NativeLocalCart | null;
    reviewedCartState: ReviewedCart | null;
    error: CartStateData["error"];
    backgroundRefreshInProgress: boolean;
}

export type ResultCartData = Result<ApiResponse<ReviewedCartApiResponse>, Error | ApiResponse>;

export type ResultNativeLocalCartData = Result<ApiResponse<NativeLocalCart>, Error | ApiResponse>;

const MENU_TO_CART_TRANSITION_SESSION_KEY = "menu_to_cart_transition_sent";

function isMenuToCartTransitionAlreadySentInSession(): boolean {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
        return false;
    }
    return sessionStorage.getItem(MENU_TO_CART_TRANSITION_SESSION_KEY) === "true";
}

function persistMenuToCartTransitionSentInSession(): void {
    if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
        return;
    }
    sessionStorage.setItem(MENU_TO_CART_TRANSITION_SESSION_KEY, "true");
}

async function getCartDetails(apiHost: string): Promise<ResultCartData> {
    return getCart(apiHost)
        .then((data) => {
            if (!data) {
                throw new Error("Received empty data from cart details");
            }
            //TODO: Remove temporary new relic event added for cross-refrencing with analytics events after analysis
            if (isClosingTimeAvailable(data)) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TIMER_RESPONSE_RECIEVED,
                    referer: TIMER_RESPONSE_SOURCE.GET_CART,
                });
            }
            return Ok(data);
        })
        .catch((err) => {
            if (isNetworkError(err)) {
                return Promise.reject(Err(err));
            }
            return Err(err) as ResultCartData;
        });
}

async function updateCartDetails(
    apiHost: string,
    params: NativeLocalCart,
    isGroupCartFlow: boolean,
): Promise<ResultCartData> {
    params.created_from_group_cart = isGroupCartFlow;
    const shouldSendMenuToCartTransition = !isMenuToCartTransitionAlreadySentInSession();
    if (shouldSendMenuToCartTransition) {
        params.is_menu_to_cart_transition = true;
        persistMenuToCartTransitionSentInSession();
    }
    return updateCartPublic(apiHost, params)
        .then((data) => {
            if (!data) {
                throw new Error("Received empty data from cart details");
            }
            //TODO: Remove temporary new relic event added for cross-refrencing with analytics events after analysis
            if (isClosingTimeAvailable(data)) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TIMER_RESPONSE_RECIEVED,
                    referer: TIMER_RESPONSE_SOURCE.UPDATE_CART,
                });
            }
            return Ok(data);
        })
        .catch((err) => {
            if (isNetworkError(err)) {
                return Promise.reject(Err(err));
            }
            if (err.status == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                return Promise.reject(Err(err));
            }
            return Err(err) as ResultCartData;
        });
}

async function getSingleOrderDetails(orderId: string): Promise<NativeLocalCart> {
    return getSingleOrder(orderId)
        .then((response) => {
            if (!response) {
                throw new Error("Received empty order details");
            }

            const localCart = Ok(response).unwrap();

            if (localCart && localCart.data) {
                const transformedResponse = prepareNativeCartParamsFromOrderDetails(localCart.data.orders[0]);
                return transformedResponse;
            }

            return {} as NativeLocalCart;
        })
        .catch((err) => {
            if (isNetworkError(err)) {
                return Promise.reject(Err(err));
            }
            if (err.status == HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                return Promise.reject(Err(err));
            }
            return Err(err);
        });
}

export interface CartError {
    httpStatusCode?: number;
    errorPayload?: unknown;
}

interface CartStateData {
    loading: boolean;
    data?: {
        localCartState: NativeLocalCart | null;
        reviewedCartState: ReviewedCart | null;
    };
    error?: CartError;
}

const useFetchCart = (): CartState => {
    const apiContextValue = useContext(ApiContext);
    const { setCartState } = useContext(CartContext);
    const { setCartToastData } = useContext(CartToastContext);
    const { isGroupCartFlow } = useContext(GroupOrderCartContext);
    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();
    const needsBackgroundRefreshRef = useRef(false);
    // Initialize to true if prefetch cache signals a background refresh is needed.
    // This prevents TurboPaymentCartWrapper from mounting before background refresh completes,
    // avoiding a double get-payment-options call.
    // Zero BAU impact: needsBackgroundRefresh is only ever set in the prefetch path.
    const [backgroundRefreshInProgress, setBackgroundRefreshInProgress] = useState(
        typeof window !== "undefined" && !!window.cartApiData?.needsBackgroundRefresh,
    );
    const [state, setState] = useState<CartStateData>({ loading: true });
    const { prepareLocalCartForUpdateCartPayload } = useLocalCartTransformer();
    const { crossSellM2SuggestionsState } = useContext(CrossSellM2Context);
    const { checkoutState } = useContext(CheckoutContext);

    const apiHost = apiContextValue.host.unwrap();

    const updateCartApiResponsehandler = useCallback(
        (response, localCart) => {
            if (isEmpty(response)) return;
            apiCallRef.current = false;
            const reviewedCart =
                response.unwrapOr({
                    statusCode: 1,
                    statusMessage: "Oops Something went wrong",
                    data: null,
                }) ?? null;

            setClosingTimeInStorage(reviewedCart);

            if (isMountedRef.current) {
                setState({
                    loading: false,
                    data: {
                        reviewedCartState: reviewedCart,
                        localCartState: reviewedCart.data
                            ? prepareNativeCartParamsFromReviewedCartResponse(reviewedCart.data, {
                                  orderComment: localCart.orderComment,
                                  cutleryComments: localCart.cutleryComments,
                                  cartLatLong: localCart.cartLatLong,
                                  sld: localCart.sld,
                                  userLiveLat: localCart.userLiveLat,
                                  userLiveLng: localCart.userLiveLng,
                                  //   date will only come in case of bulk order
                                  date: getDateFromApiResponse(reviewedCart),
                                  // eslint-disable-next-line @typescript-eslint/naming-convention
                                  p_segment_id: localCart?.p_segment_id,
                                  // eslint-disable-next-line @typescript-eslint/naming-convention
                                  parent_restaurant_id: localCart?.parent_restaurant_id,
                                  // eslint-disable-next-line @typescript-eslint/naming-convention
                                  cart_initialization_source: localCart?.cart_initialization_source,
                                  // eslint-disable-next-line @typescript-eslint/naming-convention
                                  cart_category_type: localCart?.cart_category_type,
                                  // eslint-disable-next-line @typescript-eslint/naming-convention
                                  cart_journey_ctx: {
                                      // eslint-disable-next-line @typescript-eslint/naming-convention
                                      cart_initialization_source:
                                          localCart?.cart_journey_ctx?.cart_initialization_source,
                                      // eslint-disable-next-line @typescript-eslint/naming-convention
                                      cart_category_type: localCart?.cart_journey_ctx?.cart_category_type,
                                      // eslint-disable-next-line @typescript-eslint/naming-convention
                                      request_contexts: localCart?.cart_journey_ctx?.request_contexts,
                                      // eslint-disable-next-line @typescript-eslint/naming-convention
                                      ...(localCart?.cart_journey_ctx?.additional_context && {
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          additional_context: localCart.cart_journey_ctx.additional_context,
                                      }),
                                  },
                                  // eslint-disable-next-line @typescript-eslint/naming-convention
                                  customer_selected_sla_option: localCart?.customer_selected_sla_option,
                                  // eslint-disable-next-line @typescript-eslint/naming-convention
                                  flowContext: localCart?.flowContext,
                              })
                            : null,
                    },
                    error: undefined,
                });
            }
        },
        [isMountedRef],
    );

    const updateCartApiErrorhandler = useCallback((err) => {
        const errorObj: CartError = {
            errorPayload: err?.error,
        };
        setState({ loading: false, error: errorObj });
        apiCallRef.current = false;
    }, []);

    const getCartData = useCallback(() => {
        if (checkoutState.type === CheckoutActionTypes.SHOW_CART && checkoutState.payload?.skipCartReload) {
            setState({ loading: false, data: state.data, error: state.error });
            return;
        }
        // >>>>>>>>>>>> TESTING CODE REMOVE IT BEFORE PROD >>>>>>>>>>>>
        const __IS_TEST__ = localStorage.getItem("__test_get_cart__");

        if (__IS_TEST__) {
            getCartDetails(apiHost)
                .then((cartResponse) => {
                    apiCallRef.current = false;

                    const cartData =
                        cartResponse.unwrapOr({
                            statusCode: 1,
                            statusMessage: "Oops Something went wrong",
                            data: null,
                        }) ?? null;

                    if (isMountedRef.current) {
                        setClosingTimeInStorage(cartData);

                        const existingCartJourneyCtx = state.data?.localCartState?.cart_journey_ctx;

                        setState({
                            loading: false,
                            data: {
                                reviewedCartState: cartData,
                                localCartState: cartData.data
                                    ? prepareNativeCartParamsFromReviewedCartResponse(cartData.data, {
                                          orderComment: state.data?.localCartState?.orderComment,
                                          cutleryComments: state.data?.localCartState?.cutleryComments,
                                          sld: state.data?.localCartState?.sld,
                                          userLiveLat: state.data?.localCartState?.userLiveLat,
                                          userLiveLng: state.data?.localCartState?.userLiveLng,
                                          //   date will only come in case of bulk order
                                          date: getDateFromApiResponse(cartData),
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          p_segment_id: state.data?.localCartState?.p_segment_id,
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          parent_restaurant_id: state.data?.localCartState?.parent_restaurant_id,
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          cart_initialization_source:
                                              state.data?.localCartState?.cart_initialization_source,
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          cart_category_type: state.data?.localCartState?.cart_category_type,
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          cart_journey_ctx: buildCartJourneyContext(
                                              existingCartJourneyCtx,
                                              cartData.data?.cart_context_drop,
                                          ),
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          customer_selected_sla_option:
                                              state.data?.localCartState?.customer_selected_sla_option,
                                          // eslint-disable-next-line @typescript-eslint/naming-convention
                                          flowContext: state.data?.localCartState?.flowContext,
                                      })
                                    : null,
                            },
                            error: undefined,
                        });
                    }
                })
                .catch((err) => {
                    updateCartApiErrorhandler(err);
                });
            return;
        }

        // <<<<<<<<<<<< TESTING CODE REMOVE IT BEFORE PROD <<<<<<<<<<<<

        /*
         * In case of a standalone flow such as when user is reordering from help center after cancellation
         * 1. we need to fetch order details using order id ,
         * 2. we need to make local cart first using the above information
         * 3. then, update-cart using the processed local cart
         */
        const retainedCartOrderId = getRetainedCartOrderId();
        if (!isEmpty(retainedCartOrderId)) {
            getSingleOrderDetails(retainedCartOrderId)
                .then((localCart) => {
                    if (!localCart) {
                        throw new Error("Recieved empty cart data in retained order flow");
                    }

                    // update the retained order id from header once retained cart is fetched
                    // so that subsequent cart updates correctly
                    GlobalHeaderState.updateHeaders({
                        [SwgyHeaderKeysList.retainedCartOrderId.toLowerCase()]: "",
                    });

                    updateCartDetails(apiHost, localCart, isGroupCartFlow)
                        .then((response) => {
                            updateCartApiResponsehandler(response, localCart);
                            setCartState({
                                type: CartActionTypes.UPDATE_LOCAL_CART,
                                payload: localCart,
                            });
                        })
                        .catch((err) => {
                            const httpStatusCode = err?.error?.status;
                            const errorObj: CartError = {
                                errorPayload: err.error,
                            };

                            if (httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                                errorObj.httpStatusCode = httpStatusCode;
                            }
                            apiCallRef.current = false;
                            setState({
                                loading: false,
                                error: errorObj,
                            });
                            console.error(err);
                        });
                })
                .catch((err) => {
                    updateCartApiErrorhandler(err);
                });
            return;
        }

        getBridgeSdk()
            .getLocalCart({
                tenant: {
                    marketplaceBusinessline: "Regular",
                    marketplaceCategory: "Food",
                    marketplaceId: 1,
                },
            })
            .then((_localCart) => {
                if (!_localCart) {
                    throw new Error("Recieved empty cart data from native - Non Hero Inline");
                }

                const localCart = prepareLocalCartForUpdateCartPayload(_localCart);

                if (isUserLoggedIn() && !isEmpty(localCart?.cartItems)) {
                    const cartItemsIdList = getCrossSellM2RequestBodyItems(localCart?.cartItems);

                    if (isEmpty(localCart?.restaurantId) || isEmpty(cartItemsIdList)) {
                        //TODO: remove after hero-inline c2p drop debugging is done
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.CART_INLINE_EMPTY_ITEMS,
                            restaurantId: String(localCart?.restaurantId),
                            cartItemsIdList: JSON.stringify(cartItemsIdList),
                            source: "non_hero_inline",
                        });
                    }
                }

                updateCartDetails(apiHost, localCart, isGroupCartFlow)
                    .then((response) => {
                        updateCartApiResponsehandler(response, localCart);
                    })
                    .catch((err) => {
                        const httpStatusCode = err?.error?.status;
                        const errorObj: CartError = {
                            errorPayload: err.error,
                        };

                        if (httpStatusCode === HTTP_STATUS_CODES.TOO_MANY_REQUESTS) {
                            errorObj.httpStatusCode = httpStatusCode;
                        }
                        apiCallRef.current = false;
                        setState({
                            loading: false,
                            error: errorObj,
                        });
                        console.error(err);
                    });
            })
            .catch((err) => {
                updateCartApiErrorhandler(err);
            });
    }, [
        checkoutState,
        state.data,
        state.error,
        apiHost,
        updateCartApiErrorhandler,
        updateCartApiResponsehandler,
        setCartState,
        isGroupCartFlow,
        isMountedRef,
        prepareLocalCartForUpdateCartPayload,
    ]);

    const addAPIFetchEventListeners = useCallback(() => {
        if (document != undefined) {
            document.addEventListener(CUSTOM_EVENTS.CART_API_FETCHED, () => {
                // Capture source before clearing for NR event below
                const cartApiSource = window.cartApiData?.source || "hero_inline";
                if (typeof window !== "undefined" && window.cartApiData.data) {
                    try {
                        const cartApiDataClone = cloneDeep(window.cartApiData);
                        const cartData = cartApiDataClone?.data?.data;

                        if (!cartData) {
                            recordNREvent({
                                eventType: NEWRELIC_TABLES.CART_INLINE_INVALID_LOCALCART,
                            });
                            throw new Error("Recieved empty cart data from native - Hero Inline");
                        }

                        if (isUserLoggedIn() && !isEmpty(cartData?.cart_menu_items)) {
                            const cartItemsIdList = getCrossSellM2RequestBodyItems(cartData?.cart_menu_items);

                            if (isEmpty(cartData?.restaurant_details?.id) || isEmpty(cartItemsIdList)) {
                                //TODO: remove after hero-inline c2p drop debugging is done
                                recordNREvent({
                                    eventType: NEWRELIC_TABLES.CART_INLINE_EMPTY_ITEMS,
                                    restaurantId: String(cartData?.restaurant_details?.id),
                                    cartItemsIdList: JSON.stringify(cartItemsIdList),
                                    source: "hero_inline",
                                    cartApiData: JSON.stringify(window.cartApiData),
                                });
                            }
                        }

                        // Save background refresh flag before clearing — useEffect reads it later
                        needsBackgroundRefreshRef.current = !!cartApiDataClone.needsBackgroundRefresh;

                        const nativeLocalCart = cartApiDataClone.nativeLocalCart || {};

                        const finalisePrefetchData = (localCart: Partial<NativeLocalCart>) => {
                            updateCartApiResponsehandler(Some(cartApiDataClone.data), localCart);
                            // Preserve prefetchTimestamp for CartReducer before clearing
                            window.__prefetchTimestamp__ = cartApiDataClone.prefetchTimestamp;
                            window.cartApiData.data = undefined;
                            window.cartApiData.source = undefined;
                            window.cartApiData.needsBackgroundRefresh = false;

                            // When cart is served from prefetch cache without a background refresh,
                            // no update-cart API call reaches the backend — so backend analytics
                            // events are missing. Fire a lightweight call to log the cart event.
                            if (!needsBackgroundRefreshRef.current && cartApiSource === "prefetch") {
                                logUpdateCartEvent(apiHost).catch(() => {
                                    // fire-and-forget — errors are already logged by makeDownstreamApiCall NR interceptor
                                });
                            }
                        };

                        // iOS fix: window.direct_bridge.getLocalCart() only returns
                        // cart_prefetched_response on iOS — nativeLocalCart is missing fields
                        // like orderComment, cutleryComments, cartItems, etc.
                        // Fetch the full native cart via async bridge and merge before rendering.
                        const isMissingNativeCartFields =
                            cartApiDataClone.source === "prefetch" &&
                            (!nativeLocalCart?.cartItems || !nativeLocalCart?.restaurantId);

                        if (isMissingNativeCartFields) {
                            getBridgeSdk()
                                .getLocalCart({
                                    tenant: {
                                        marketplaceBusinessline: "Regular",
                                        marketplaceCategory: "Food",
                                        marketplaceId: 1,
                                    },
                                })
                                .then((fullNativeCart) => {
                                    if (!fullNativeCart) {
                                        finalisePrefetchData(nativeLocalCart);
                                        recordNREvent({
                                            eventType: NEWRELIC_TABLES.PREFETCH_CART,
                                            type: "ios_native_cart_fallback_failed",
                                            reason: "bridge_resolved_null",
                                        });
                                        return;
                                    }

                                    const mergedLocalCart = { ...nativeLocalCart, ...fullNativeCart };
                                    finalisePrefetchData(mergedLocalCart);
                                    recordNREvent({
                                        eventType: NEWRELIC_TABLES.PREFETCH_CART,
                                        type: "ios_native_cart_fallback",
                                        hasOrderComment: String(!!mergedLocalCart.orderComment),
                                        hasCutleryComments: String(!!mergedLocalCart.cutleryComments),
                                    });
                                })
                                .catch(() => {
                                    finalisePrefetchData(nativeLocalCart);
                                    recordNREvent({
                                        eventType: NEWRELIC_TABLES.PREFETCH_CART,
                                        type: "ios_native_cart_fallback_failed",
                                    });
                                });
                        } else {
                            finalisePrefetchData(nativeLocalCart);
                        }
                    } catch (e) {
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.HERO_INLINE_EVENT_CAPTURE_ERROR,
                            body: JSON.stringify(e),
                        });
                        captureError(e);
                    }
                }
                recordNREvent({
                    eventType: NEWRELIC_TABLES.INLINE_WEB_CART_API_STATE,
                    state: CUSTOM_EVENTS.CART_API_FETCHED,
                    source: cartApiSource,
                });
            });
            document.addEventListener(CUSTOM_EVENTS.CART_API_ERROR, () => {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.INLINE_WEB_CART_API_STATE,
                    state: CUSTOM_EVENTS.CART_API_ERROR,
                });
                getCartData();
            });
            document.addEventListener(CUSTOM_EVENTS.FETCH_API_ERROR, () => {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.INLINE_WEB_CART_API_STATE,
                    state: CUSTOM_EVENTS.FETCH_API_ERROR,
                });
                getCartData();
            });
        }
    }, [updateCartApiResponsehandler, getCartData, apiHost]);

    useEffect(() => {
        /*
            Added a check for cross sell loading phase as when cross sell context was
            changing then get-cart was getting called twice due to this in local
        */
        if (!state.loading || apiCallRef.current || crossSellM2SuggestionsState.isFetchingSuggestions) {
            return;
        }

        apiCallRef.current = true;
        if (typeof window !== "undefined") {
            const xpHeroApiInline = window.isHeroApiInlineHtmlXp;

            // Handling backward compatibility
            if (
                isEmpty(xpHeroApiInline) ||
                xpHeroApiInline === "false" ||
                xpHeroApiInline === "NO_XP" ||
                !window.direct_bridge
            ) {
                if (
                    window.cartApiData?.success &&
                    window.cartApiData?.source === "prefetch" &&
                    window.cartApiData?.data?.data
                ) {
                    addAPIFetchEventListeners();
                    document.dispatchEvent(new CustomEvent(CUSTOM_EVENTS.CART_API_FETCHED));
                    return;
                }
                getCartData();
                return;
            }

            addAPIFetchEventListeners();
        }

        /*  Below steps if api fetched events were fired before listeners were attached
         *    1. Early Return on Loading State
         *    2. Handle Error State - fallback to get-cart
         *    3. Handle Success State - proceed to render saved response instead of calling update-cart
         */

        // The Hero Api Call will be considered terminated when any one of (success, error, fetchError) flag are set to TRUE
        // All the other cases will be ignored
        const isHeroInlineCallInProgress =
            typeof window !== "undefined" &&
            !isEmpty(window.cartApiData) &&
            !window.cartApiData.success &&
            !window.cartApiData.error &&
            !window.cartApiData.fetchError;
        if (isHeroInlineCallInProgress) {
            return;
        }

        const cartApiData = window.cartApiData;

        if (
            !cartApiData ||
            isEmpty(cartApiData.data) ||
            !cartApiData.supported || // if fetch wasn't supported or fetch error . fetch api here
            cartApiData.fetchError ||
            cartApiData.error
        ) {
            getCartData();
            return;
        }

        // If JS chunk is downloaded after the inline call is completed the we dispatch the success event again
        if (document != undefined && typeof window !== "undefined" && window.isHeroApiInlineHtmlXp === "true") {
            const successEvent = new CustomEvent(CUSTOM_EVENTS.CART_API_FETCHED);
            document.dispatchEvent(successEvent);
        }
    }, [
        getCartData,
        state.loading,
        addAPIFetchEventListeners,
        updateCartApiResponsehandler,
        crossSellM2SuggestionsState.isFetchingSuggestions,
    ]);

    // Background refresh when prefetch cache is stale
    useEffect(() => {
        if (state.loading || !state.data?.reviewedCartState || !needsBackgroundRefreshRef.current) {
            return;
        }

        // Clear the flag so this only runs once
        needsBackgroundRefreshRef.current = false;

        const rawLocalCart = state.data.localCartState;
        if (!rawLocalCart) {
            setBackgroundRefreshInProgress(false);
            return;
        }

        // Transform local cart to include cart_update_coupon_request (same as BAU flow).
        // Without this, couponCode from native is not converted to the structured
        // cart_update_coupon_request field, causing backend to re-auto-apply coupons.
        const localCart = prepareLocalCartForUpdateCartPayload(rawLocalCart);

        setBackgroundRefreshInProgress(true);

        recordNREvent({
            eventType: NEWRELIC_TABLES.PREFETCH_CART,
            type: "bg_refresh_start",
            reason: window.cartApiData?.backgroundRefreshReason ?? "--",
            cartId: String(state.data?.reviewedCartState?.data?.cartId ?? "--"),
        });

        updateCartDetails(apiHost, localCart, isGroupCartFlow)
            .then((response) => {
                if (isMountedRef.current) {
                    // Also update CartContext so the rest of the cart UI re-renders
                    const reviewedCart = response.unwrapOr({
                        statusCode: 1,
                        statusMessage: "Oops Something went wrong",
                        data: null,
                    });

                    // HTTP errors are swallowed by fetchData and returned as resolved promises
                    // with an empty object {} (no statusCode, no data).
                    if (reviewedCart?.statusCode === undefined && !reviewedCart?.data) {
                        setCartToastData({ message: OOPS_SOMETHING_WENT_WRONG, show: true });
                        setBackgroundRefreshInProgress(false);
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.PREFETCH_CART,
                            type: "bg_refresh_error",
                            error: "empty response - HTTP error",
                            httpStatusCode: "--",
                        });
                        return;
                    }

                    // Backend returned a response but with null data.
                    // Don't update cart state with null data — keep showing prefetch response.
                    if (!reviewedCart?.data) {
                        setBackgroundRefreshInProgress(false);
                        recordNREvent({
                            eventType: NEWRELIC_TABLES.PREFETCH_CART,
                            type: "bg_refresh_error",
                            error: "null data in response",
                            statusCode: String(reviewedCart?.statusCode ?? "--"),
                            statusMessage: String(reviewedCart?.statusMessage ?? "--"),
                        });
                        return;
                    }

                    updateCartApiResponsehandler(response, localCart);

                    const oldTotal = state.data?.reviewedCartState?.data?.cart_total;
                    const newTotal = reviewedCart?.data?.cart_total;
                    const oldItemCount = state.data?.reviewedCartState?.data?.cart_menu_items?.length ?? 0;
                    const newItemCount = reviewedCart?.data?.cart_menu_items?.length ?? 0;
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.PREFETCH_CART,
                        type: "bg_refresh_success",
                        cartChanged: String(oldTotal !== newTotal || oldItemCount !== newItemCount),
                        oldTotal: String(oldTotal ?? "--"),
                        newTotal: String(newTotal ?? "--"),
                        oldItemCount: String(oldItemCount),
                        newItemCount: String(newItemCount),
                        statusCode: String(reviewedCart?.statusCode ?? "--"),
                        cartId: String(reviewedCart?.data?.cartId ?? "--"),
                    });
                    setCartState({
                        type: CartActionTypes.UPDATE_CART,
                        payload: {
                            uniqueIdForCart: randomStringIdGenerator(),
                            localCart: prepareNativeCartParamsFromReviewedCartResponse(reviewedCart.data, {
                                orderComment: localCart.orderComment,
                                cutleryComments: localCart.cutleryComments,
                                cartLatLong: localCart.cartLatLong,
                                sld: localCart.sld,
                                userLiveLat: localCart.userLiveLat,
                                userLiveLng: localCart.userLiveLng,
                                date: getDateFromApiResponse(reviewedCart),
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                p_segment_id: localCart?.p_segment_id,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                parent_restaurant_id: localCart?.parent_restaurant_id,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                cart_initialization_source: localCart?.cart_initialization_source,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                cart_category_type: localCart?.cart_category_type,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                cart_journey_ctx: localCart?.cart_journey_ctx,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                customer_selected_sla_option: localCart?.customer_selected_sla_option,
                                flowContext: localCart?.flowContext,
                            }),
                            reviewedCart,
                        },
                    });
                    setBackgroundRefreshInProgress(false);
                }
            })
            .catch((err) => {
                if (isMountedRef.current) {
                    setBackgroundRefreshInProgress(false);
                    setCartToastData({ message: OOPS_SOMETHING_WENT_WRONG, show: true });
                }
                recordNREvent({
                    eventType: NEWRELIC_TABLES.PREFETCH_CART,
                    type: "bg_refresh_error",
                    error: err?.message || String(err),
                    httpStatusCode: String(err?.error?.status ?? err?.status ?? "--"),
                });
            });
    }, [
        state.loading,
        state.data,
        apiHost,
        isGroupCartFlow,
        isMountedRef,
        updateCartApiResponsehandler,
        setCartState,
        setCartToastData,
    ]);

    return {
        localCartState: state.data?.localCartState ?? null,
        reviewedCartState: state.data?.reviewedCartState ?? null,
        loading: state.loading,
        error: state.error,
        backgroundRefreshInProgress,
    };
};

export default useFetchCart;
